import React, { useState, useEffect } from 'react';
import { Crown, Calendar, Clock, Zap, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES, PLAN_DETAILS } from '../../lib/constants';

/**
 * SubscriptionCard - Shows subscription status with countdown
 * Displays days remaining for premium users
 */
const SubscriptionCard = ({ profile, subscription }) => {
  const navigate = useNavigate();
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [percentageLeft, setPercentageLeft] = useState(100);

  // Determine plan details
  const planType = profile?.subscription_plan || 'free';
  const isPremium = ['weekly', 'monthly', 'semester'].includes(planType.toLowerCase());

  // Calculate days remaining
  useEffect(() => {
    if (!isPremium || !subscription?.end_date) {
      setDaysRemaining(null);
      setPercentageLeft(0);
      return;
    }

    const calculateDays = () => {
      const now = new Date();
      const endDate = new Date(subscription.end_date);
      const startDate = new Date(subscription.start_date);
      
      const totalMs = endDate - startDate;
      const remainingMs = endDate - now;
      
      const days = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
      const percentage = Math.max(0, Math.min(100, (remainingMs / totalMs) * 100));
      
      setDaysRemaining(Math.max(0, days));
      setPercentageLeft(percentage);
    };

    calculateDays();
    
    // Update every hour
    const interval = setInterval(calculateDays, 1000 * 60 * 60);
    
    return () => clearInterval(interval);
  }, [isPremium, subscription]);

  // Get plan display name
  const getPlanDisplayName = () => {
    if (!isPremium) return 'Free Plan';
    
    const planKey = planType.toUpperCase();
    return PLAN_DETAILS[planKey]?.name || 'Premium Plan';
  };

  // Get color based on days remaining
  const getStatusColor = () => {
    if (!isPremium) return 'gray';
    if (daysRemaining > 30) return 'green';
    if (daysRemaining > 7) return 'yellow';
    return 'red';
  };

  const statusColor = getStatusColor();
  
  // Free Plan Display
  if (!isPremium) {
    return (
      <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-sm border border-cream-300/50 p-4 sm:p-5 md:p-6">
        <div className="text-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <Calendar className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
          </div>
          
          <h3 className="text-base sm:text-lg font-bold text-brown-900 mb-1">
            Free Plan
          </h3>
          
          <p className="text-xs sm:text-sm text-brown-600 mb-4 sm:mb-5">
            3 uploads per day • Basic summaries
          </p>
          
          <button
            onClick={() => navigate(ROUTES.SETTINGS)}
            className="w-full px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg sm:rounded-xl transition-all shadow-md font-semibold text-sm sm:text-base flex items-center justify-center space-x-2"
          >
            <Crown className="w-4 h-4" />
            <span>Upgrade to Premium</span>
          </button>
        </div>
      </div>
    );
  }

  // Premium Plan Display with Countdown
  return (
    <div className="backdrop-blur-sm bg-gradient-to-br from-yellow-50/50 to-orange-50/30 rounded-xl sm:rounded-2xl shadow-md border-2 border-yellow-400/30 p-4 sm:p-5 md:p-6">
      
      {/* Premium Badge */}
      <div className="flex items-center justify-center mb-4">
        <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs sm:text-sm font-bold rounded-full shadow-md">
          <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span>{getPlanDisplayName().toUpperCase()}</span>
        </div>
      </div>

      {/* Days Remaining - Big Number */}
      <div className="text-center mb-4 sm:mb-5">
        <div className="flex items-center justify-center space-x-2 mb-2">
          <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-brown-600" />
          <h3 className="text-base sm:text-lg font-semibold text-brown-900">
            Time Remaining
          </h3>
        </div>
        
        <p className="text-4xl sm:text-5xl md:text-6xl font-bold text-brown-900 mb-1">
          {daysRemaining !== null ? daysRemaining : '-'}
        </p>
        
        <p className="text-sm sm:text-base text-brown-600">
          {daysRemaining === 1 ? 'day left' : 'days left'}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-4 sm:mb-5">
        <div className="flex justify-between text-xs sm:text-sm text-brown-600 mb-2">
          <span>Subscription Progress</span>
          <span>{Math.round(percentageLeft)}%</span>
        </div>
        
        <div className="w-full bg-cream-200/50 rounded-full h-2.5 sm:h-3 overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              statusColor === 'green' ? 'bg-gradient-to-r from-green-500 to-green-600' :
              statusColor === 'yellow' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
              'bg-gradient-to-r from-red-500 to-red-600'
            }`}
            style={{ width: `${percentageLeft}%` }}
          />
        </div>
      </div>

      {/* Expiry Date */}
      {subscription?.end_date && (
        <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-brown-700 mb-4">
          <Calendar className="w-4 h-4" />
          <span>
            Expires: {new Date(subscription.end_date).toLocaleDateString('en-NG', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </span>
        </div>
      )}

      {/* Warning if expiring soon */}
      {daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0 && (
        <div className="bg-yellow-100/50 border border-yellow-300/50 rounded-lg p-3 mb-4">
          <div className="flex items-start space-x-2">
            <Zap className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-yellow-800">
                Subscription expiring soon!
              </p>
              <p className="text-xs text-yellow-700 mt-0.5">
                Renew now to keep unlimited access
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Expired */}
      {daysRemaining === 0 && (
        <div className="bg-red-100/50 border border-red-300/50 rounded-lg p-3 mb-4">
          <div className="flex items-start space-x-2">
            <Clock className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-red-800">
                Subscription expired
              </p>
              <p className="text-xs text-red-700 mt-0.5">
                Renew to continue enjoying premium features
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Manage Subscription Button */}
      <button
        onClick={() => navigate(ROUTES.SETTINGS)}
        className="w-full px-4 sm:px-5 py-2 sm:py-2.5 bg-white/60 hover:bg-white/80 text-brown-700 rounded-lg transition-all font-medium text-sm sm:text-base border border-brown-200/50"
      >
        Manage Subscription
      </button>

      {/* Premium Benefits Reminder */}
      <div className="mt-4 pt-4 border-t border-brown-200/30">
        <p className="text-xs text-brown-600 text-center mb-2">Your benefits:</p>
        <div className="flex flex-wrap gap-2 justify-center">
          <span className="inline-flex items-center space-x-1 px-2 py-1 bg-brown-100/50 text-brown-700 text-xs rounded-full">
            <TrendingUp className="w-3 h-3" />
            <span>Unlimited uploads</span>
          </span>
          <span className="inline-flex items-center space-x-1 px-2 py-1 bg-brown-100/50 text-brown-700 text-xs rounded-full">
            <Zap className="w-3 h-3" />
            <span>Extended summaries</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCard;